<section class="super-image-studio">
  <header class="studio-header">
    <div class="studio-title">
      <span class="studio-badge">Studio Plus</span>
      <h1>Super Image Studio</h1>
      <p>Generate, edit, and refine with SD3.5 + Stability tools in a camera-inspired workflow.</p>
    </div>
    <div class="studio-header-actions">
      <button class="ghost-button" type="button" (click)="toggleSettings()">
        <i class="fa-solid fa-sliders"></i>
        Settings
      </button>
      <button class="ghost-button" type="button" (click)="clearAll()">
        <i class="fa-solid fa-rotate"></i>
        Reset
      </button>
    </div>
  </header>

  <div class="studio-body">
    <div class="preview-column">
      <div class="preview-card">
        <div class="preview-top">
          <div class="preview-label">Output</div>
          <div class="preview-status" [class.active]="isLoading">
            <span class="status-dot"></span>
            <span>{{ isLoading ? 'Processing' : 'Ready' }}</span>
          </div>
        </div>

        <div class="preview-frame" [class.has-image]="!!resultUrl">
          <ng-container *ngIf="resultUrl; else emptyPreview">
            <img class="preview-image" [src]="resultUrl" alt="Studio output" />
            <div class="preview-meta" *ngIf="resultMeta.modelId">
              <span>{{ resultMeta.modelId }}</span>
              <span *ngIf="resultMeta.seed !== undefined">Seed: {{ resultMeta.seed }}</span>
            </div>
          </ng-container>
          <ng-template #emptyPreview>
            <div class="empty-preview">
              <i class="fa-solid fa-camera-retro"></i>
              <p>Capture a result with the shutter.</p>
              <button class="primary-button" type="button" (click)="shoot()" [disabled]="isLoading">
                Shutter
              </button>
            </div>
          </ng-template>
        </div>

        <div class="mode-strip">
          <button
            class="mode-chip"
            type="button"
            *ngFor="let mode of modes"
            [class.active]="mode.key === selectedMode"
            (click)="setMode(mode.key)"
          >
            {{ mode.label }}
          </button>
        </div>

        <div class="shutter-row">
          <button class="shutter-button" type="button" (click)="shoot()" [disabled]="isLoading">
            <span class="shutter-ring"></span>
          </button>
          <div class="shutter-label">Shoot</div>
        </div>
      </div>

      <div class="preview-card">
        <div class="preview-top">
          <div class="preview-label">Inputs</div>
          <div class="preview-status subtle">Provide base, mask, or reference when required.</div>
        </div>
        <div class="input-grid">
          <label class="input-tile">
            <span>Base image</span>
            <input type="file" accept="image/*" (change)="onPickBase($event)" />
            <img *ngIf="basePreviewUrl" [src]="basePreviewUrl" alt="Base preview" />
            <p *ngIf="!basePreviewUrl">Optional for generation, required for edits.</p>
          </label>
          <label class="input-tile">
            <span>Mask</span>
            <input type="file" accept="image/*" (change)="onPickMask($event)" />
            <img *ngIf="maskPreviewUrl" [src]="maskPreviewUrl" alt="Mask preview" />
            <p *ngIf="!maskPreviewUrl">Used for inpaint + erase.</p>
          </label>
          <label class="input-tile">
            <span>Reference</span>
            <input type="file" accept="image/*" (change)="onPickRef($event)" />
            <img *ngIf="refPreviewUrl" [src]="refPreviewUrl" alt="Reference preview" />
            <p *ngIf="!refPreviewUrl">For style guide/transfer.</p>
          </label>
        </div>
      </div>
    </div>

    <div class="controls-column">
      <div class="control-card">
        <div class="control-header">
          <h3>Prompt</h3>
          <span class="mode-indicator">{{ activeMode.label }}</span>
        </div>
        <label>
          Prompt
          <textarea rows="3" [(ngModel)]="prompt" placeholder="Describe the scene, lighting, and mood"></textarea>
        </label>
        <label>
          Negative prompt
          <input type="text" [(ngModel)]="negativePrompt" placeholder="Avoid elements" />
        </label>
      </div>

      <div class="control-card" *ngIf="showSettings">
        <div class="control-header">
          <h3>Settings</h3>
          <button class="ghost-button" type="button" (click)="toggleSettings()">Hide</button>
        </div>
        <div class="settings-grid">
          <label>
            Aspect ratio
            <select [(ngModel)]="aspectRatio">
              <option value="1:1">1:1</option>
              <option value="16:9">16:9</option>
              <option value="21:9">21:9</option>
              <option value="2:3">2:3</option>
              <option value="3:2">3:2</option>
              <option value="4:5">4:5</option>
              <option value="5:4">5:4</option>
              <option value="9:16">9:16</option>
              <option value="9:21">9:21</option>
            </select>
          </label>
          <label>
            Output format
            <select [(ngModel)]="outputFormat">
              <option value="png">PNG</option>
              <option value="jpeg">JPEG</option>
              <option value="webp">WEBP</option>
            </select>
          </label>
          <label>
            Seed
            <input type="number" [(ngModel)]="seed" placeholder="Optional" />
          </label>
          <label>
            Strength / Intensity
            <input type="range" min="0" max="1" step="0.05" [(ngModel)]="strength" />
            <span class="range-meta">{{ strength }}</span>
          </label>
        </div>

        <div class="model-row">
          <label>
            Generation model
            <select [(ngModel)]="selectedGenModel">
              <option *ngFor="let option of genModelOptions" [value]="option.key">{{ option.label }}</option>
            </select>
          </label>
          <p class="helper-text">Generation modes only. Edit tools are auto-routed by operation.</p>
        </div>
      </div>

      <div class="control-card">
        <div class="control-header">
          <h3>History</h3>
          <button class="ghost-button" type="button" (click)="clearHistory()">Clear</button>
        </div>
        <div class="history-grid" *ngIf="history$ | async as history">
          <button class="history-card" type="button" *ngFor="let item of history" (click)="useHistory(item)">
            <img [src]="item.image" alt="History output" />
            <div>
              <strong>{{ item.operation }}</strong>
              <small>{{ item.prompt || 'No prompt' }}</small>
            </div>
          </button>
          <p class="helper-text" *ngIf="history.length === 0">No captures yet.</p>
        </div>
      </div>

      <div class="status-card" *ngIf="error">
        <div class="status-error">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <p>{{ error }}</p>
        </div>
      </div>
    </div>
  </div>
</section>
